import ChangeBeneficiaryEmailMessage from "components/shared/ChangeBeneficiaryEmailMessage";
import CustomFieldsCollapse from "components/shared/CustomFieldsCollapse";
import AutocompleteFormComponent, { renderOption } from "components/shared/FormComponent/AutocompleteFormComponent";
import DateFormComponent from "components/shared/FormComponent/DateFormComponent";
import ImageFormComponent from "components/shared/FormComponent/ImageFormComponent";
import TextFormComponent from "components/shared/FormComponent/TextFormComponent";
import InfoTooltip from "components/shared/InfoTooltip";
import MessageBlock from "components/shared/MessageBlock";
import LocaleAutocomplete from "components/shared/NameTranslationWrapper/LocaleAutocomplete";
import { PortalType } from "contexts/portal.context";
import { t } from "i18next";
import type { CSSProperties } from "react";
import { useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { entityList, getEntityNames } from "store/beneficiaryStore";
import type { ICustomField } from "store/customFieldsStore";
import { useAppSelector } from "store/store";
import { EPortalType } from "store/types";

import type { TBeneficiaryFormData } from "../types";

interface IPersonalInfoStep {
    /**
     * React useForm() hooks
     */
    useFormData: TBeneficiaryFormData;
    stepTitle: string;
    stepSubtitle: string;
    indexStep: number;
    /**
     * This prop is the name of the component mode.
     */
    mode: "new" | "edit-page";
    /** Array with fields belonging to the section */
    customFields: ICustomField[];
    isLoginEmailChanging?: boolean;
    hasPendingEmail?: boolean;
    pendingEmailValue?: string | null;
    accountStatus?: "pending" | "noaccount" | "confirmed";
    watchedLoginEmail: string | null;
}

/**
 * Labels for name, surname and avatar field depending on entity type (legal or physical)
 **/
export const labelFor = () => ({
    physical: {
        name: t("NewBeneficiary.Name"),
        surname: t("NewBeneficiary.Surname"),
        avatarUrl: t("NewBeneficiary.Photo"),
    },
    none: {
        name: t("NewBeneficiary.Name"),
        surname: t("NewBeneficiary.Surname"),
        avatarUrl: t("NewBeneficiary.Photo"),
    },
    legal: {
        name: t("NewBeneficiary.RepresentativeName"),
        surname: t("NewBeneficiary.CompanyName"),
        avatarUrl: t("NewBeneficiary.CompanyLogo"),
    },
});

const PersonalInfoStep = ({
    useFormData,
    stepTitle,
    stepSubtitle,
    indexStep,
    mode,
    customFields,
    isLoginEmailChanging,
    hasPendingEmail,
    pendingEmailValue,
    accountStatus,
    watchedLoginEmail,
}: IPersonalInfoStep) => {
    const [t] = useTranslation();
    const entityNames = getEntityNames(t);
    const portalType = useContext(PortalType);
    const { sendPictureStatus, beneficiar } = useAppSelector((state) => state.beneficiary);
    const { control, errors, watch, setValue } = useFormData;
    const entity = watch("entity") ?? "";

    // Filter fields that should not appear on creation :
    const customFieldsOnCreation = useMemo(() => {
        return customFields.filter((field) => field.showOnCreation);
    }, [customFields]);

    const displayLoginEmail = !!beneficiar.loginEmail;

    const linkToDoc =
        portalType === EPortalType.BOOK
            ? "https://helpdesk.crealo.app/fr/article/modifier-un-beneficiaire-1059e06/"
            : "https://presse-helpdesk.crealo.app/fr/article/modifier-un-beneficiaire-rkoc9t/";

    return (
        <>
            <h3 className="form-section-title">
                {indexStep + 1}. {stepTitle}
                {mode === "edit-page" && (
                    <InfoTooltip text={t("TransferTerms.Helpdesk.AccessDoc")} link={linkToDoc} iconType="question" />
                )}
            </h3>
            <span className="form-section-description">{stepSubtitle}</span>

            <div className="form-fields">
                <ImageFormComponent
                    label={mode === "new" ? labelFor()[entity].avatarUrl : ""}
                    avatar={watch("avatarUrl")}
                    setAvatar={(name: string, value: any) => setValue && setValue("avatarUrl", value)}
                    sendPictureStatus={sendPictureStatus}
                    control={control}
                    controlName="avatarUrl"
                    type={mode === "new" ? "new-beneficiary" : "beneficiary"}
                />
                <AutocompleteFormComponent
                    control={control}
                    label={t("NewBeneficiary.Entity")}
                    controlName="entity"
                    options={entityList}
                    renderOption={(props, option) => renderOption(props, option, entityNames)}
                    renderOptionText={(option) => entityNames[option]}
                    isOptionEqualToValue={(option, value) => option === value}
                    error={errors.entity}
                    disableClearable
                    required
                />
                <TextFormComponent
                    label={labelFor()[entity].name}
                    control={control}
                    controlName="name"
                    error={errors.name}
                />
                <TextFormComponent
                    label={labelFor()[entity].surname}
                    control={control}
                    controlName="surname"
                    required
                    error={errors.surname}
                />
                {!["audiovisual", "press"].includes(portalType ?? "") && (
                    <TextFormComponent
                        label={t("ImportData.BenPseudonym")}
                        control={control}
                        controlName="pseudonym"
                        error={errors.pseudonym}
                    />
                )}
                {entity === "physical" ? (
                    <div className="form-component-wrapper rib">
                        <TextFormComponent
                            label={t("NewBeneficiary.PhoneNumber")}
                            control={control}
                            controlName="phoneNumber"
                            placeholder="06 05 04 03 02"
                            error={errors.phoneNumber}
                        />
                        <DateFormComponent
                            label={t("NewBeneficiary.BirthDate")}
                            control={control}
                            controlName="birthDate"
                            error={errors.birthDate}
                        />
                    </div>
                ) : (
                    <TextFormComponent
                        label={t("NewBeneficiary.PhoneNumber")}
                        control={control}
                        controlName="phoneNumber"
                        placeholder="06 05 04 03 02"
                        error={errors.phoneNumber}
                    />
                )}
                <div
                    className="email-field-wrapper"
                    style={{
                        position: "relative",
                        marginBottom: isLoginEmailChanging || hasPendingEmail ? "10px" : "0px",
                    }}
                >
                    <TextFormComponent
                        label={t("NewBeneficiary.Email")}
                        control={control}
                        controlName="email"
                        error={errors.email}
                        placeholder={t("NewBeneficiary.EmailHere")}
                        disabled={isLoginEmailChanging}
                        infoTooltip={{ type: "info", text: t("NewBeneficiary.EmailRequired") }}
                    />

                    {displayLoginEmail && (
                        <TextFormComponent
                            className="mt-4"
                            label={t("NewBeneficiary.LoginEmail")}
                            control={control}
                            controlName="loginEmail"
                            error={errors.loginEmail}
                            placeholder={t("NewBeneficiary.EmailHere")}
                            infoTooltip={{ type: "info", text: t("NewBeneficiary.LoginEmailRequired") }}
                            required
                        />
                    )}

                    {isLoginEmailChanging && accountStatus === "confirmed" && (
                        <ChangeBeneficiaryEmailMessage
                            currentEmail={beneficiar.loginEmail ?? ""}
                            newEmail={watchedLoginEmail}
                            style={{ marginTop: "10px" }}
                        />
                    )}

                    {hasPendingEmail && !isLoginEmailChanging && (
                        <MessageBlock
                            type="warning"
                            small
                            text={t("EmailChanges.PendingRequest", { email: pendingEmailValue })}
                            style={
                                {
                                    marginTop: "8px",
                                    backgroundColor: "#FFF7E6",
                                    borderColor: "#FFE58F",
                                } as CSSProperties
                            }
                        />
                    )}
                </div>
                <LocaleAutocomplete
                    label={t("NewBeneficiary.Language")}
                    placeholder={t("NewBeneficiary.ChooseLanguage")}
                    control={control}
                    controlName="lang"
                    error={errors.lang}
                    infoTooltip={{
                        text: t("NewBeneficiary.LanguageTooltip"),
                        type: "question",
                    }}
                />

                {/** COLLAPSE FOR THE CUSTOM FIELDS */}
                {customFields.length > 0 && (
                    <CustomFieldsCollapse
                        sectionFields={mode === "new" ? customFieldsOnCreation : customFields}
                        errors={errors}
                        mode={mode}
                    />
                )}
            </div>
        </>
    );
};

export default PersonalInfoStep;
